import { ArrowDown, ArrowUp, ArrowUpDown, Info } from 'lucide-react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ColumnFilter } from '../ColumnFilter';
import type { ColumnConfig, TableRowData, TableHeaderProps } from '../../types';
import {
  hasFilterOptions,
  resolveFilterOptions,
} from '../../utils/tableHeaderUtils';

interface HeaderCellContentProps<TRow extends TableRowData> {
  column: ColumnConfig<TRow>;
  headerText: string;
  filters: TableHeaderProps<TRow>['filters'];
  onFilterChange: TableHeaderProps<TRow>['onFilterChange'];
  sort?: TableHeaderProps<TRow>['sort'];
  onSortChange?: TableHeaderProps<TRow>['onSortChange'];
  enableSorting: boolean;
  /** Centers the header content. Used for group columns. */
  centered?: boolean;
}

export function HeaderCellContent<TRow extends TableRowData>({
  column,
  headerText,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  enableSorting,
  centered = false,
}: HeaderCellContentProps<TRow>) {
  const sortField = column.sortField ?? column.id;
  const isSortable = Boolean(enableSorting && column.sortable && sortField);
  const isSorted = Boolean(sort && sortField && sort.field === sortField);
  const sortOrder = isSorted ? sort?.order : undefined;
  const SortIcon = isSorted
    ? sortOrder === SortOrder.Asc
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;

  const showFilter = hasFilterOptions(column);
  const resolvedFilterOptions = resolveFilterOptions(column);
  const currentFilter = filters.find(f => f.field === column.id);
  const range = column.range;

  return (
    <div
      className={cn('flex items-center gap-1', centered && 'justify-center')}
    >
      <span>{headerText}</span>
      {column.description && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`${headerText} info`}
              className="inline-flex items-center justify-center rounded-sm p-0.5 text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Info className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent align="start" className="max-w-64">
            <p>{column.description}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {isSortable && sortField && (
        <button
          type="button"
          onClick={() => onSortChange?.(sortField)}
          className={cn(
            'inline-flex items-center justify-center rounded-sm p-0.5 text-muted-foreground/70 transition-colors hover:text-foreground',
            isSorted && 'text-accent-foreground'
          )}
          aria-label={`Sort by ${column.id}`}
        >
          <SortIcon className="h-3 w-3" />
        </button>
      )}
      {showFilter &&
        resolvedFilterOptions &&
        column.filterable === true &&
        column.filterOperators && (
          <ColumnFilter
            fieldId={column.id}
            fieldName={headerText}
            description={column.description}
            filterOptions={resolvedFilterOptions}
            availableOperators={column.filterOperators}
            columnDataType={column.dataType}
            range={range}
            currentFilter={currentFilter}
            onFilterChange={onFilterChange}
          />
        )}
    </div>
  );
}
